/* eslint-disable no-use-before-define */
import { useMemo } from 'react';
import _ from 'underscore';
import { useAccountFetch, useCustomerFetch, useUserFetch } from '../use-entity';
import { getInitializerValues } from '../initializers';

// format any pre-existing data in a way that can be understood by the ABL component;
// also make sure to load the entities that will have to be updated later,
// since we need to send certain (non-editable) properties back, e.g. Timestamps
export default function useDefaults({ scope, initializers, prefetchedEntities }) {
  const entitiesToFetch = getEntitiesToFetch(prefetchedEntities);

  const {
    customer,
    account,
    user,
    errors,
  } = useDataFetch(scope, entitiesToFetch, prefetchedEntities);

  const isLoading = _.any(getPendingEntities({
    entitiesToFetch,
    customer,
    account,
    user,
    errors,
  }));

  return useMemo(() => {
    const { defaults, overrides } = getInitializerValues(initializers);

    return {
      isLoading,
      errors,
      hasErrors: () => errors.account || errors.customer || errors.user,
      baseline: {
        account,
        customer,
        user,
      },
      data: {
        countryCode: !_.isUndefined(overrides.country) ? overrides.country : ((account && account.BusinessAddress && account.BusinessAddress.Country) || defaults.country),
        businessNameData: {
          businessName: !_.isUndefined(overrides.name) ? overrides.name : ((customer && !isPlaceholder(customer.Name) ? customer.Name : null) || defaults.name),
        },
        currencyData: {
          currencyCode: !_.isUndefined(overrides.currency) ? overrides.currency : ((account && account.CurrencyType) || defaults.currency),
        },
        taxData: account && {
          data: account.TaxInformation,
          taxCertification: account.TaxCertification,
        },
        addressData: {
          // NOTE: there are some critical differences between the behavior of underscore defaults() and the standard JS object aggregation;
          // here we need to ignore null objects and properties that are present, but "undefined"
          address: !_.isUndefined(overrides.address) ? overrides.address : _.defaults({}, account && undefineMissingProps(account.BusinessAddress), defaults.address),
        },
        phoneNumberData: {
          phoneNumberWithCallingCode: !_.isUndefined(overrides.phone) ? overrides.phone : ((customer && customer.BusinessPhone) || defaults.phone),
        },
        timeZoneData: {
          timeZone: (!_.isUndefined(overrides.timeZone) ? overrides.timeZone.toString(10) : (account && account.TimeZone && account.TimeZone.toString(10))) || null,
        },
        userAddressData: {
          address: user && user.Address,
        },
      },
    };
  }, [initializers, account, customer, user, isLoading, errors]);
}

function getEntitiesToFetch(prefetchedEntities) {
  // NOTE: we always need to fetch account & customer data, because of the address timestamps, timezones etc.
  const entities = ['account', 'customer', 'user'];

  if (!prefetchedEntities) {
    return entities;
  }

  return _.filter(entities, entity => !prefetchedEntities[entity]);
}

function useDataFetch(scope, entitiesToFetch, prefetchedEntities) {
  const { data: accountFetched, error: accountError } = useAccountFetch(scope, () => _.includes(entitiesToFetch, 'account'));
  const { data: customerFetched, error: customerError } = useCustomerFetch(scope, () => _.includes(entitiesToFetch, 'customer'));
  const { data: userFetched, error: userError } = useUserFetch(scope, () => _.includes(entitiesToFetch, 'user'));

  const account = (prefetchedEntities && prefetchedEntities.account) || accountFetched;
  const customer = (prefetchedEntities && prefetchedEntities.customer) || customerFetched;
  const user = (prefetchedEntities && prefetchedEntities.user) || userFetched;

  const errors = useMemo(() => ({
    account: accountError,
    customer: customerError,
    user: userError,
  }), [accountError, customerError, userError]);

  return {
    account,
    customer,
    user,
    errors,
  };
}

function getPendingEntities({
  entitiesToFetch,
  customer,
  account,
  user,
  errors,
}) {
  const pendingEntities = [];

  if (_.includes(entitiesToFetch, 'account') && !account && !errors.account) {
    pendingEntities.push('account');
  }

  if (_.includes(entitiesToFetch, 'customer') && !customer && !errors.customer) {
    pendingEntities.push('customer');
  }

  if (_.includes(entitiesToFetch, 'user') && !user && !errors.user) {
    pendingEntities.push('user');
  }

  return pendingEntities;
}

// because certain entities need to be created before the user had a chance to provide some input,
// their names will be automatically generated; we don't want to display these placeholder names
// when the user finally has the chance to edit the information
function isPlaceholder(name) {
  return name && name.startsWith('_') && name.includes('_TEMP_');
}

// NOTE: some address fields might have been set as empty or null strings when the information was missing;
// for the purpose of deciding proper initialization values, we need to revert these back to an undefined state
function undefineMissingProps(address) {
  return _.mapObject(address, value => (value === '' || value === null ? undefined : value));
}
